<!-- TradingCheckBoard.vue (oder dein Komponenten-Name)
     Komplett inkl. DAILY TRADES Anzeige über dem Board
-->
<template>
  <v-container fluid class="pa-4">
    <v-row class="mb-4">
      <v-col>
        <h2>Trading-Check Board (neuer Kauf)</h2>
        <div class="text-caption">
          ✅ Checklist + automatische Checks (Positionsgröße, Risiko, Konzentration)
        </div>
      </v-col>

      <v-col class="text-right">
        <v-btn color="primary" variant="tonal" :loading="loadingPortfolio" @click="loadPortfolioToday">
          Depot neu laden
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="portfolioError" type="error" variant="tonal" class="mb-4">
      {{ portfolioError }}
    </v-alert>

    <!-- ===================== DAILY TRADES (NEU) ===================== -->
    <v-card variant="outlined" class="mb-6">
      <v-card-title class="d-flex align-center justify-space-between">
        <span>📌 Daily Trades Empfehlungen</span>

        <div class="d-flex align-center ga-2">
          <span class="text-caption">
            Stand: <strong>{{ dailyTradesDate }}</strong>
            <span v-if="dailyTradesSource">· Quelle: {{ dailyTradesSource }}</span>
          </span>

          <v-btn size="small" variant="text" :loading="dailyTradesLoading" @click="loadDailyTrades">
            Refresh
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="dailyTradesErrorText" type="error" variant="tonal" class="mb-4">
          {{ dailyTradesErrorText }}
        </v-alert>

        <div v-if="dailyTradesLoading" class="text-caption py-2">Lade Empfehlungen…</div>

        <v-row v-else>
          <!-- BUYS -->
          <v-col cols="12" md="6">
            <div class="text-subtitle-2 mb-2">✅ BUY</div>

            <v-alert v-if="!dailyBuys.length" type="info" variant="tonal" class="mb-2">
              Keine BUY-Vorschläge vorhanden.
            </v-alert>

            <v-card
              v-for="(t, idx) in dailyBuys"
              :key="'buy-' + idx + '-' + t.stocks_name"
              variant="tonal"
              class="mb-2"
            >
              <v-card-text class="py-3">
                <div class="d-flex align-center justify-space-between">
                  <div class="text-body-1"><strong>{{ t.stocks_name }}</strong></div>
                  <div class="text-caption">Score: <strong>{{ t.score }}</strong></div>
                </div>

                <div v-if="getDailyTradeStrategyTag(t)" class="mt-2">
                  <v-chip
                    size="x-small"
                    :color="getDailyTradeStrategyTag(t).color"
                    :variant="getDailyTradeStrategyTag(t).variant"
                  >
                    {{ getDailyTradeStrategyTag(t).label }}
                  </v-chip>
                </div>

                <div v-if="getDailyTradeStrategyTag(t)" class="mt-2">
                  <v-chip
                    size="x-small"
                    :color="getDailyTradeStrategyTag(t).color"
                    :variant="getDailyTradeStrategyTag(t).variant"
                  >
                    {{ getDailyTradeStrategyTag(t).label }}
                  </v-chip>
                </div>

                <div class="text-caption mt-1">
                  last30: <strong>{{ t.last_30_pct }}%</strong> · Jahr: <strong>{{ t.perf_jahr }}%</strong> · Monat:
                  <strong>{{ t.perf_monat }}%</strong>
                </div>

                <div v-if="Array.isArray(t.why) && t.why.length" class="text-caption mt-1">
                  why: <strong>{{ t.why.join(", ") }}</strong>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- SELLS -->
          <v-col cols="12" md="6">
            <div class="text-subtitle-2 mb-2">⚠️ SELL</div>

            <v-alert v-if="!dailySells.length" type="info" variant="tonal" class="mb-2">
              Keine SELL-Vorschläge vorhanden.
            </v-alert>

            <v-card
              v-for="(t, idx) in dailySells"
              :key="'sell-' + idx + '-' + t.stocks_name"
              variant="tonal"
              class="mb-2"
            >
              <v-card-text class="py-3">
                <div class="d-flex align-center justify-space-between">
                  <div class="text-body-1"><strong>{{ t.stocks_name }}</strong></div>
                  <div class="text-caption">Score: <strong>{{ t.score }}</strong></div>
                </div>

                <div class="text-caption mt-1">
                  last30: <strong>{{ t.last_30_pct }}%</strong> · Jahr: <strong>{{ t.perf_jahr }}%</strong> · Monat:
                  <strong>{{ t.perf_monat }}%</strong>
                </div>

                <div v-if="Array.isArray(t.why) && t.why.length" class="text-caption mt-1">
                  why: <strong>{{ t.why.join(", ") }}</strong>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <!-- ===================== /DAILY TRADES (NEU) ===================== -->

    <!-- Header Inputs -->
    <v-card variant="outlined" class="mb-6">
      <v-card-title class="d-flex align-center justify-space-between">
        <span>Trade-Input</span>
        <span class="text-caption">
          Depot-Stichtag: <strong>{{ portfolioDate }}</strong> · Depotwert:
          <strong>{{ formatEUR(portfolioValue) }}</strong>
        </span>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-combobox
              v-model="symbol"
              :items="stockNameItems"
              label="Titel / Symbol"
              density="compact"
              placeholder="Tippen oder aus Depot wählen…"
              clearable
              hide-details
            /></v-col>

          <v-col cols="12" md="4">
            <v-select
              v-model="category"
              :items="categoryItems"
              label="Kategorie (Pflicht)"
              density="compact"
              hide-details
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="plannedBuyEur"
              type="number"
              label="Geplanter Kaufbetrag (€)"
              density="compact"
              hide-details
            />
            <div class="mt-1 d-flex align-center ga-2">
              <v-chip v-if="suggestedBuyEur > 0" size="small" variant="tonal">
                Vorschlag: {{ formatEUR(suggestedBuyEur) }}
              </v-chip>
              <v-btn
                v-if="suggestedBuyEur > 0"
                size="small"
                variant="text"
                @click="applySuggestedBuy"
              >
                Übernehmen
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="stopDistanceEur"
              type="number"
              label="Stop-Abstand (€ pro Position)"
              density="compact"
              hint="Abstand in € (Einstand → Stop) * Positionsmenge, als Gesamtverlust."
              persistent-hint
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              v-model="stopType"
              :items="stopTypeItems"
              label="Stop-Art"
              density="compact"
              hide-details
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-switch
              v-model="marketSupport"
              label="Marktumfeld unterstützt den Trade"
              density="compact"
              hide-details
            />
          </v-col>
        </v-row>

        <!-- Ampel Summary -->
        <v-row class="mt-4">
          <v-col cols="12" md="3">
            <v-card :variant="ampelVariant(maxPositionCheck.level)" class="h-100">
              <v-card-text>
                <div class="text-caption">Max. Depotanteil</div>
                <div class="text-h6">{{ maxPositionCheck.label }}</div>
                <div class="text-caption">
                  Geplant: <strong>{{ plannedPositionPct.toFixed(2) }}%</strong> · Limit:
                  <strong>{{ (categoryLimitPct ?? 0).toFixed(0) }}%</strong>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card :variant="ampelVariant(riskCheck.level)" class="h-100">
              <v-card-text>
                <div class="text-caption">Max. Verlust (1% Regel)</div>
                <div class="text-h6">{{ riskCheck.label }}</div>
                <div class="text-caption">
                  Max Verlust: <strong>{{ formatEUR(maxLossEur) }}</strong> · Stop-Abstand:
                  <strong>{{ formatEUR(stopDistanceEur || 0) }}</strong>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card :variant="ampelVariant(concentrationCheck.level)" class="h-100">
              <v-card-text>
                <div class="text-caption">Top-5 Konzentration nach Kauf</div>
                <div class="text-h6">{{ concentrationCheck.label }}</div>
                <div class="text-caption">
                  Aktuell: <strong>{{ top5CurrentPct.toFixed(1) }}%</strong> · Nach Kauf:
                  <strong>{{ top5AfterPct.toFixed(1) }}%</strong>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card :variant="ampelVariant(setupCheck.level)" class="h-100">
              <v-card-text>
                <div class="text-caption">Setup-Qualität</div>
                <div class="text-h6">{{ setupCheck.label }}</div>
                <div class="text-caption">
                  Trend/Spike/Markt: <strong>{{ setupScore }}/3</strong>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-alert v-if="autoInterpretation" type="info" variant="tonal" class="mt-4">
          {{ autoInterpretation }}
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- CHECKLISTE -->
    <v-card variant="outlined" class="mb-6">
      <v-card-title>✅ TRADING-CHECKLISTE (1 Seite)</v-card-title>
      <v-card-text>
        <!-- 1) Titel Einordnung -->
        <v-card variant="tonal" class="mb-4">
          <v-card-title class="text-subtitle-1">1️⃣ Titel-Einordnung (Pflicht)</v-card-title>
          <v-card-text>
            <v-checkbox v-model="checks.category_set" label="Kategorie festgelegt" density="compact" hide-details />

            <v-row class="mt-2">
              <v-col cols="12" md="6">
                <div class="text-caption mb-1">Kategorie</div>
                <v-radio-group v-model="category" density="compact" hide-details>
                  <v-radio label="Trend-Core (Large Cap, klarer Marktführer)" value="trend_core" />
                  <v-radio label="Trend-Satellite (Growth, höhere Vola)" value="trend_satellite" />
                  <v-radio label="Speculative (extreme Vola / Event-Risk)" value="speculative" />
                  <v-radio label="Stabilisator (Silver, Bonds)" value="stabilizer" />
                </v-radio-group>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-caption mb-1">Max. Depotanteil (Regel)</div>
                <div class="text-body-2">
                  Trend-Core: ≤ 12% · Trend-Satellite: ≤ 7% · Speculative: ≤ 3% · Stabilisator: flexibel
                </div>

                <v-checkbox
                  v-model="checks.max_position_ok"
                  :label="`Max. Depotanteil eingehalten (Limit ${categoryLimitPct ?? '–'}%)`"
                  density="compact"
                  hide-details
                />
                <v-checkbox
                  v-model="checks.top5_ok"
                  label="Top-5-Konzentration nach Kauf ≤ 45%"
                  density="compact"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- 2) Setup Qualität -->
        <v-card variant="tonal" class="mb-4">
          <v-card-title class="text-subtitle-1">2️⃣ Setup-Qualität (kein Setup → kein Trade)</v-card-title>
          <v-card-text>
            <v-checkbox v-model="checks.trend_intact" label="Trend intakt (höhere Hochs & Tiefs oder klarer Ausbruch)" density="compact" hide-details />
            <v-checkbox v-model="checks.no_vertical_spike" label="Kauf NICHT nach vertikalem Spike" density="compact" hide-details />
            <v-checkbox v-model="checks.market_ok" label="Marktumfeld unterstützt den Trade (Sektor nicht klar schwächer als Markt)" density="compact" hide-details />
          </v-card-text>
        </v-card>

        <!-- 3) Stop Logik -->
        <v-card variant="tonal" class="mb-4">
          <v-card-title class="text-subtitle-1">3️⃣ Stop-Logik (vor Kauf festlegen!)</v-card-title>
          <v-card-text>
            <v-checkbox v-model="checks.stop_defined" label="Stop-Art definiert" density="compact" hide-details />

            <v-row class="mt-2">
              <v-col cols="12" md="6">
                <div class="text-caption mb-1">Stop-Art (Regel-Reminder)</div>
                <v-list density="compact" class="py-0">
                  <v-list-item>
                    <v-list-item-title>Trend-Core: Struktur-Stop (Wochentief / 20–50 DMA)</v-list-item-title>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Trend-Satellite: ATR-/Vola-Stop</v-list-item-title>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Speculative: harter Verlust-Stop oder kein Trade</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="12" md="6">
                <v-checkbox v-model="checks.stop_not_too_tight" label="Stop NICHT enger als Marktvolatilität" density="compact" hide-details />
                <v-checkbox v-model="checks.stop_distance_calc" :label="`Stop-Abstand in € klar berechnet: ${formatEUR(stopDistanceEur || 0)}`" density="compact" hide-details />
                <v-checkbox v-model="checks.max_loss_ok" label="Max. Verlust ≤ 1% Depot" density="compact" hide-details />

                <div class="text-caption mt-2">
                  Positionsgröße = (Depot × 1%) ÷ Stop-Abstand
                </div>
                <div class="text-body-2">
                  Empfohlene Positionsgröße (max): <strong>{{ formatEUR(suggestedBuyEur) }}</strong>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- 4) Positionsgröße & Timing -->
        <v-card variant="tonal" class="mb-4">
          <v-card-title class="text-subtitle-1">4️⃣ Positionsgröße & Timing</v-card-title>
          <v-card-text>
            <v-checkbox v-model="checks.position_size_correct" label="Positionsgröße korrekt berechnet" density="compact" hide-details />
            <v-checkbox v-model="checks.not_bigger_after_losses" label="Nicht größer nach Verlustserie" density="compact" hide-details />
            <v-checkbox v-model="checks.no_feelings_buy" label="Kein „Gefühlskauf“ (Größe nur aus Rechnung)" density="compact" hide-details />

            <div class="text-caption mt-3 mb-1">Einstieg idealerweise</div>
            <v-checkbox v-model="checks.entry_pullback" label="nach Pullback" density="compact" hide-details />
            <v-checkbox v-model="checks.entry_consolidation" label="nach Konsolidierung" density="compact" hide-details />
            <v-checkbox v-model="checks.entry_breakout" label="oder bestätigtem Ausbruch (nicht Vorwegnahme)" density="compact" hide-details />
          </v-card-text>
        </v-card>

        <!-- 5) Gewinn & Exit Plan -->
        <v-card variant="tonal" class="mb-4">
          <v-card-title class="text-subtitle-1">5️⃣ Gewinn- & Exit-Plan (vor Kauf!)</v-card-title>
          <v-card-text>
            <v-checkbox v-model="checks.breakeven_rule" label="Break-Even-Regel: bei +25–30% → Stop auf Einstand" density="compact" hide-details />
            <v-checkbox v-model="checks.partial_sell_rule" label="Teilverkaufs-Regel: bei +50–70% → 25–33% verkaufen" density="compact" hide-details />
            <v-checkbox v-model="checks.trailing_stop" label="Restposition läuft mit Trailing-Stop (kein fixes Kursziel)" density="compact" hide-details />
          </v-card-text>
        </v-card>

        <!-- 6) Re-Entry Regeln -->
        <v-card variant="tonal">
          <v-card-title class="text-subtitle-1">6️⃣ Re-Entry-Regeln (extrem wichtig)</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-switch v-model="reentryAllowed" label="Re-Entry erlaubt?" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="8">
                <v-checkbox v-model="checks.reentry_higher_low" label="Nur erlaubt bei: neuem höheren Tief" density="compact" hide-details />
                <v-checkbox v-model="checks.reentry_new_high" label="oder neuem Hoch nach Konsolidierung" density="compact" hide-details />
                <v-checkbox v-model="checks.reentry_max2" label="Max. 2 Re-Entries pro Trend" density="compact" hide-details />
                <v-checkbox v-model="checks.reentry_3stops_pause" label="3 Stops in Folge? → 20 Handelstage Pause" density="compact" hide-details />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- Optional: kleines “Status”-Panel -->
    <v-card variant="outlined">
      <v-card-title>Status</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <div class="text-caption">Checklist erfüllt</div>
            <div class="text-h6">{{ checksDone }}/{{ checksTotal }}</div>
          </v-col>

          <v-col cols="12" md="8">
            <div class="text-caption">Schnell-Fazit</div>
            <div class="text-body-1">
              <strong>{{ finalVerdict }}</strong>
            </div>
            <div class="text-caption mt-1">
              Hinweis: Das Board ist “Prozess-Qualität”, keine Marktprognose.
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useStore } from "vuex";

const store = useStore();

const loadingPortfolio = computed(() => store.getters.statePortfolioLoading);
const portfolioErrObj = computed(() => store.getters.statePortfolioError);
const portfolioError = computed(() => (portfolioErrObj.value ? String(portfolioErrObj.value) : null));

const portfolioAtDate = computed(() => store.getters.statePortfolioAtDate);
const portfolioValue = computed(() => Number(portfolioAtDate.value?.wert_gesamt || 0));
const portfolioPositions = computed(() =>
  Array.isArray(portfolioAtDate.value?.positionen) ? portfolioAtDate.value.positionen : []
);
const portfolioDate = computed(() =>
  portfolioAtDate.value?.stichtag ? String(portfolioAtDate.value.stichtag).slice(0, 10) : "—"
);

// Bereits gekaufte Stocks (aus deinem existing Store-Modul)
const stocks = computed(() => (Array.isArray(store.getters.stateStocks) ? store.getters.stateStocks : []));
const stockNameItems = computed(() => {
  const names = stocks.value
    .map((s) => String(s.stocks_name || s.symbol || "").trim())
    .filter(Boolean);
  return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, "de"));
});

// ---------------- DAILY TRADES (NEU) ----------------
const dailyTrades = computed(() => store.getters.stateDailyTrades);
const dailyTradesLoading = computed(() => store.getters.stateDailyTradesLoading);
const dailyTradesErrObj = computed(() => store.getters.stateDailyTradesError);
const dailyTradesAll = computed(() => store.getters.stateDailyTradesAll);

const dailyTradesErrorText = computed(() => {
  const e = dailyTradesErrObj.value;
  if (!e) return null;
  // axios error -> möglichst lesbar
  if (e?.response?.data) return typeof e.response.data === "string" ? e.response.data : JSON.stringify(e.response.data);
  if (e?.message) return String(e.message);
  return String(e);
});

const dailyTradesDate = computed(() => {
  const dt = dailyTrades.value?.generated_at;
  if (!dt) return "—";
  return String(dt).slice(0, 19).replace("T", " ");
});
const dailyTradesSource = computed(() => dailyTrades.value?.source || "");

const dailyBuys = computed(() => (Array.isArray(dailyTrades.value?.buys) ? dailyTrades.value.buys : []));
const dailySells = computed(() => (Array.isArray(dailyTrades.value?.sells) ? dailyTrades.value.sells : []));
const dailyAllByStockName = computed(() => {
  const map = new Map();
  const rows = Array.isArray(dailyTradesAll.value?.rows) ? dailyTradesAll.value.rows : [];

  for (const row of rows) {
    const name = String(row?.stocks_name || "").trim();
    if (name && !map.has(name)) map.set(name, row);
  }

  return map;
});

async function loadDailyTrades() {
  try {
    await store.dispatch("getDailyTrades");
    await store.dispatch("getDailyTradesAll");
  } catch (e) {
    console.error("getDailyTrades failed", e);
  }
}

function getDailyTradeAnalysis(tradeItem) {
  const name = String(tradeItem?.stocks_name || "").trim();
  if (!name) return null;
  return dailyAllByStockName.value.get(name) || null;
}

function getDailyTradeStrategyTag(tradeItem) {
  const analysis = getDailyTradeAnalysis(tradeItem);
  if (!analysis) return null;

  const winner = String(analysis?.better_strategy || "");
  const isLowQuality = String(analysis?.result_quality || "").toLowerCase() === "low";

  if (winner === "actual_strategy") {
    return {
      label: isLowQuality ? "Aktive Strategie besser ?" : "Aktive Strategie besser",
      color: "success",
      variant: isLowQuality ? "outlined" : "flat",
    };
  }

  if (winner === "buy_and_hold_benchmark") {
    return {
      label: isLowQuality ? "Buy and Hold besser ?" : "Buy and Hold besser",
      color: "warning",
      variant: isLowQuality ? "outlined" : "flat",
    };
  }

  if (winner === "tie") {
    return {
      label: "Gleichstand",
      color: "info",
      variant: "outlined",
    };
  }

  return null;
}
// ---------------------------------------------------

function todayYMD() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

async function loadPortfolioToday() {
  await store.dispatch("getPortfolioAtDate", todayYMD());
}

onMounted(() => {
  // Auto-load Portfolio
  if (!portfolioAtDate.value) loadPortfolioToday();

  // Stocks fürs Dropdown laden (falls leer)
  if (!stocks.value.length) {
    store.dispatch("getStocks").catch((e) => console.error("getStocks failed", e));
  }

  // Daily trades laden
  loadDailyTrades();
});

// ---------- Inputs ----------
const symbol = ref("");
const category = ref(null);
const plannedBuyEur = ref(0);
const stopDistanceEur = ref(0);
const stopType = ref(null);
const marketSupport = ref(true);

const categoryItems = [
  { title: "Trend-Core (≤ 12%)", value: "trend_core" },
  { title: "Trend-Satellite (≤ 7%)", value: "trend_satellite" },
  { title: "Speculative (≤ 3%)", value: "speculative" },
  { title: "Stabilisator (flexibel)", value: "stabilizer" },
];

const stopTypeItems = [
  { title: "Struktur-Stop (Wochentief / 20–50 DMA)", value: "structure" },
  { title: "ATR-/Vola-Stop", value: "atr" },
  { title: "Harter Verlust-Stop", value: "hard" },
];

// ---------- Checklist state ----------
const checks = reactive({
  // 1) Einordnung
  category_set: false,
  max_position_ok: false,
  top5_ok: false,

  // 2) Setup
  trend_intact: false,
  no_vertical_spike: false,
  market_ok: true,

  // 3) Stop
  stop_defined: false,
  stop_not_too_tight: false,
  stop_distance_calc: false,
  max_loss_ok: false,

  // 4) Größe/Timing
  position_size_correct: false,
  not_bigger_after_losses: false,
  no_feelings_buy: false,
  entry_pullback: false,
  entry_consolidation: false,
  entry_breakout: false,

  // 5) Exit Plan
  breakeven_rule: false,
  partial_sell_rule: false,
  trailing_stop: false,

  // 6) Re-entry
  reentry_higher_low: false,
  reentry_new_high: false,
  reentry_max2: false,
  reentry_3stops_pause: false,
});

const reentryAllowed = ref(false);

// Auto-sync für einige Pflichtfelder
watch(category, (v) => {
  checks.category_set = !!v;
});

// ---------- Helpers ----------
function formatEUR(x) {
  const n = Number(x || 0);
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n);
}

function ampelVariant(level) {
  if (level === "green") return "tonal";
  if (level === "yellow") return "outlined";
  return "outlined";
}

// ---------- Core calculations ----------
const categoryLimitPct = computed(() => {
  if (category.value === "trend_core") return 12;
  if (category.value === "trend_satellite") return 7;
  if (category.value === "speculative") return 3;
  if (category.value === "stabilizer") return null; // flexibel
  return null;
});

const plannedPositionPct = computed(() => {
  const pv = portfolioValue.value || 0;
  const buy = Number(plannedBuyEur.value || 0);
  if (pv <= 0 || buy <= 0) return 0;
  return (buy / pv) * 100;
});

const maxLossEur = computed(() => (portfolioValue.value || 0) * 0.01);

const impliedStopPct = computed(() => {
  const buy = Number(plannedBuyEur.value || 0);
  const stopLoss = Number(stopDistanceEur.value || 0);
  if (buy <= 0 || stopLoss <= 0) return 0;
  return stopLoss / buy;
});

const suggestedBuyEur = computed(() => {
  const pv = portfolioValue.value || 0;
  if (pv <= 0) return 0;

  const limitPct = categoryLimitPct.value;
  const maxByCategory = limitPct ? (pv * limitPct) / 100 : Infinity;

  const stopPct = impliedStopPct.value;
  const maxByRisk = stopPct > 0 ? maxLossEur.value / stopPct : Infinity;

  const best = Math.min(maxByCategory, maxByRisk);
  return Number.isFinite(best) && best > 0 ? Math.floor(best) : 0;
});

function applySuggestedBuy() {
  if (suggestedBuyEur.value > 0) plannedBuyEur.value = suggestedBuyEur.value;
}

// Top-5 Konzentration aktuell (Gewichte nach 'wert')
const top5CurrentPct = computed(() => {
  const pv = portfolioValue.value || 0;
  if (pv <= 0) return 0;
  const values = portfolioPositions.value
    .map((p) => Number(p.wert || 0))
    .filter((v) => v > 0)
    .sort((a, b) => b - a);
  const top5 = values.slice(0, 5).reduce((s, v) => s + v, 0);
  return (top5 / pv) * 100;
});

// Top-5 nach Kauf: add new position value (geplanter Kaufbetrag)
const top5AfterPct = computed(() => {
  const pv = portfolioValue.value || 0;
  const buy = Number(plannedBuyEur.value || 0);
  if (pv <= 0) return top5CurrentPct.value;

  const pvAfter = pv + Math.max(0, buy);

  const values = portfolioPositions.value
    .map((p) => Number(p.wert || 0))
    .filter((v) => v > 0);

  if (buy > 0) values.push(buy);

  values.sort((a, b) => b - a);
  const top5 = values.slice(0, 5).reduce((s, v) => s + v, 0);
  return (top5 / pvAfter) * 100;
});

// ---------- Ampel Checks ----------
const maxPositionCheck = computed(() => {
  if (!category.value) return { level: "red", label: "Kategorie fehlt" };
  if (category.value === "stabilizer") return { level: "green", label: "OK (flexibel)" };

  const limit = categoryLimitPct.value;
  const pct = plannedPositionPct.value;

  if (!limit) return { level: "yellow", label: "Unklar" };
  if (pct <= limit) return { level: "green", label: "OK" };
  if (pct <= limit * 1.25) return { level: "yellow", label: "Zu groß" };
  return { level: "red", label: "Viel zu groß" };
});

const riskCheck = computed(() => {
  const stop = Number(stopDistanceEur.value || 0);
  if (stop <= 0) return { level: "red", label: "Stop-Abstand fehlt" };

  const ok = stop <= maxLossEur.value + 1e-9;
  if (ok) return { level: "green", label: "OK" };
  if (stop <= maxLossEur.value * 1.25) return { level: "yellow", label: "Grenzwertig" };
  return { level: "red", label: "Zu hoher Verlust" };
});

const concentrationCheck = computed(() => {
  const after = top5AfterPct.value;
  if (after <= 45) return { level: "green", label: "OK" };
  if (after <= 55) return { level: "yellow", label: "Hoch" };
  return { level: "red", label: "Zu konzentriert" };
});

const setupScore = computed(() => {
  let s = 0;
  if (checks.trend_intact) s += 1;
  if (checks.no_vertical_spike) s += 1;
  if (checks.market_ok && marketSupport.value) s += 1;
  return s;
});

const setupCheck = computed(() => {
  const s = setupScore.value;
  if (s === 3) return { level: "green", label: "Gut" };
  if (s === 2) return { level: "yellow", label: "OK" };
  return { level: "red", label: "Schwach" };
});

const autoInterpretation = computed(() => {
  const blockers = [];
  if (maxPositionCheck.value.level === "red") blockers.push("Positionsgröße über Limit");
  if (riskCheck.value.level === "red") blockers.push("Stop/Risiko verletzt 1%-Regel");
  if (concentrationCheck.value.level === "red") blockers.push("Top-5 Konzentration zu hoch");
  if (setupCheck.value.level === "red") blockers.push("Setup-Qualität zu schwach");

  if (blockers.length === 0) return "✅ Trade wirkt prozessseitig solide: Größe/Stop/Konzentration/Setup sind im grünen Bereich.";
  if (blockers.length === 1) return `⚠️ Prüfen vor Kauf: ${blockers[0]}.`;
  return `⛔ Mehrere Red Flags: ${blockers.join(", ")}. Lieber anpassen oder Trade auslassen.`;
});

// Auto-tick einiger Checkboxen basierend auf Berechnungen
watch([maxPositionCheck, concentrationCheck, riskCheck, stopType, stopDistanceEur], () => {
  checks.max_position_ok = maxPositionCheck.value.level !== "red";
  checks.top5_ok = top5AfterPct.value <= 45;

  checks.stop_defined = !!stopType.value;
  checks.stop_distance_calc = Number(stopDistanceEur.value || 0) > 0;
  checks.max_loss_ok = riskCheck.value.level !== "red";
});

// ---------- Checklist summary ----------
const checksTotal = computed(() => Object.keys(checks).length);
const checksDone = computed(() => Object.values(checks).filter(Boolean).length);

const finalVerdict = computed(() => {
  if (maxPositionCheck.value.level === "red" || riskCheck.value.level === "red" || concentrationCheck.value.level === "red") {
    return "STOP: Erst Positionsgröße/Stop/Konzentration korrigieren.";
  }
  if (setupCheck.value.level === "red") return "Vorsicht: Setup nicht überzeugend – Trade eher auslassen.";
  if (checksDone.value / checksTotal.value >= 0.75) return "OK: Checkliste überwiegend erfüllt.";
  return "Noch offen: Checkliste ist unvollständig.";
});
</script>

<style scoped>
h2 {
  margin: 0;
}
</style>
